import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Icon from "@mui/material/Icon";
import { useState, useEffect } from "react";

const ACCENT_YELLOW = "#FFC107";
const NAVY = "#061D36";
const NAVY_MID = "#0f2c4a";
const TITLE_COLOR = "#0f2c4a";
const TEXT_MUTED = "#5a6b85";

const activeItemSx = (isActive) => ({
  bgcolor: isActive ? NAVY_MID : "transparent",
  boxShadow: isActive ? "0 2px 10px rgba(6, 29, 54, 0.18)" : "none",
  "&:hover": {
    bgcolor: isActive ? NAVY : "rgba(0, 0, 0, 0.04)",
  },
});

function Sidebar({ menuStructure, activeSection, activeSubsection, activeSubSubsection, onSectionClick, onSubsectionClick, onSubSubsectionClick }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedSubsections, setExpandedSubsections] = useState({});

  // Deschide în cuprins secțiunea activă și subsecțiunea selectată (inclusiv la load
  // sau când utilizatorul apasă linkuri I.1 / I.2 în conținutul din dreapta).
  useEffect(() => {
    if (!activeSection) return;

    const section = menuStructure.find((s) => s.id === activeSection);
    if (section?.subsections?.length) {
      setExpandedSections((prev) => ({ ...prev, [activeSection]: true }));
    }

    if (activeSubsection) {
      setExpandedSubsections((prev) => ({ ...prev, [activeSubsection]: true }));
    }
  }, [activeSection, activeSubsection, activeSubSubsection, menuStructure]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleSubsection = (subsectionId) => {
    setExpandedSubsections(prev => ({
      ...prev,
      [subsectionId]: !prev[subsectionId]
    }));
  };

  const handleSectionClick = (sectionId, hasSubsections) => {
    onSectionClick(sectionId);
    onSubsectionClick(null); // Reset subsection
    onSubSubsectionClick(null); // Reset sub-subsection
    if (hasSubsections) {
      toggleSection(sectionId);
    }
  };

  const handleSubsectionClick = (sectionId, subsectionId, hasSubSubsections) => {
    onSectionClick(sectionId);
    onSubsectionClick(subsectionId);
    onSubSubsectionClick(null); // Reset sub-subsection
    if (hasSubSubsections) {
      toggleSubsection(subsectionId);
    }
  };

  const handleSubSubsectionClick = (sectionId, subsectionId, subSubsectionId) => {
    onSectionClick(sectionId);
    onSubsectionClick(subsectionId);
    onSubSubsectionClick(subSubsectionId);
  };

  return (
    <SoftBox
      sx={{
        borderRight: { xs: "none", md: "1px solid rgba(6, 29, 54, 0.1)" },
        borderBottom: { xs: "1px solid rgba(6, 29, 54, 0.1)", md: "none" },
        p: 3,
        minHeight: { xs: "auto", md: "600px" },
        maxHeight: { md: "80vh" },
        overflowY: "auto",
        position: "sticky",
        top: { md: 100 },
      }}
    >
      <SoftTypography variant="h6" fontWeight="bold" sx={{ color: TITLE_COLOR }} mb={2}>
        Cuprins
      </SoftTypography>

      {menuStructure.map((section) => (
        <SoftBox key={section.id} mb={1}>
          <SoftBox
            onClick={() => handleSectionClick(section.id, section.subsections && section.subsections.length > 0)}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
              ...activeItemSx(activeSection === section.id),
            }}
          >
            <SoftBox display="flex" alignItems="center" justifyContent="space-between">
              <SoftTypography
                variant="button"
                fontWeight={activeSection === section.id ? "bold" : "medium"}
                sx={{
                  fontSize: "0.875rem",
                  color: activeSection === section.id ? ACCENT_YELLOW : TITLE_COLOR,
                  opacity: activeSection === section.id ? 1 : 0.82,
                }}
              >
                {section.title}
              </SoftTypography>
              {section.subsections && section.subsections.length > 0 && (
                <Icon fontSize="small" sx={{ color: activeSection === section.id ? ACCENT_YELLOW : TEXT_MUTED }}>
                  {expandedSections[section.id] ? "expand_less" : "expand_more"}
                </Icon>
              )}
            </SoftBox>
          </SoftBox>

          {/* Subsections */}
          {section.subsections && expandedSections[section.id] && (
            <SoftBox ml={2} mt={0.5}>
              {section.subsections.map((subsection) => (
                <SoftBox key={subsection.id} mb={0.5}>
                  <SoftBox
                    onClick={() => handleSubsectionClick(section.id, subsection.id, subsection.subsubsections && subsection.subsubsections.length > 0)}
                    sx={{
                      p: 1,
                      pl: 2,
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      ...activeItemSx(activeSubsection === subsection.id),
                    }}
                  >
                    <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                      <SoftTypography
                        variant="caption"
                        fontWeight={activeSubsection === subsection.id ? "bold" : "regular"}
                        sx={{ color: activeSubsection === subsection.id ? ACCENT_YELLOW : TEXT_MUTED }}
                      >
                        {subsection.title}
                      </SoftTypography>
                      {subsection.subsubsections && subsection.subsubsections.length > 0 && (
                        <Icon sx={{ fontSize: "16px", color: activeSubsection === subsection.id ? ACCENT_YELLOW : TEXT_MUTED }}>
                          {expandedSubsections[subsection.id] ? "expand_less" : "expand_more"}
                        </Icon>
                      )}
                    </SoftBox>
                  </SoftBox>

                  {/* Sub-subsections */}
                  {subsection.subsubsections && expandedSubsections[subsection.id] && (
                    <SoftBox ml={2} mt={0.5}>
                      {subsection.subsubsections.map((subsubsection) => (
                        <SoftBox key={subsubsection.id} mb={0.5}>
                          <SoftBox
                            onClick={() => handleSubSubsectionClick(section.id, subsection.id, subsubsection.id)}
                            sx={{
                              p: 0.8,
                              pl: 2,
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              ...activeItemSx(activeSubSubsection === subsubsection.id),
                            }}
                          >
                            <SoftTypography
                              variant="caption"
                              fontWeight={activeSubSubsection === subsubsection.id ? "medium" : "regular"}
                              sx={{
                                fontSize: "0.75rem",
                                color: activeSubSubsection === subsubsection.id ? ACCENT_YELLOW : TEXT_MUTED,
                              }}
                            >
                              • {subsubsection.title}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  )}
                </SoftBox>
              ))}
            </SoftBox>
          )}
        </SoftBox>
      ))}
    </SoftBox>
  );
}

Sidebar.propTypes = {
  menuStructure: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subsections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subsubsections: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
      })),
    })),
  })).isRequired,
  activeSection: PropTypes.string.isRequired,
  activeSubsection: PropTypes.string,
  activeSubSubsection: PropTypes.string,
  onSectionClick: PropTypes.func.isRequired,
  onSubsectionClick: PropTypes.func.isRequired,
  onSubSubsectionClick: PropTypes.func.isRequired,
};

export default Sidebar;

